import React from 'react';

const sizes = {
  sm: 'h-4 w-4',
  md: 'h-8 w-8',
  lg: 'h-12 w-12',
  xl: 'h-16 w-16',
};

const colors = {
  primary: 'text-blue-600',
  secondary: 'text-slate-600',
  white: 'text-white',
  success: 'text-emerald-600',
  warning: 'text-amber-600',
  error: 'text-rose-600',
};

const Spinner = ({
  size = 'md',
  color = 'primary',
  className = '',
  label,
}) => {
  const sizeClasses = sizes[size] || sizes.md;
  const colorClasses = colors[color] || colors.primary;

  return (
    <div className={`inline-flex flex-col items-center ${className}`}>
      <svg
        className={`animate-spin ${sizeClasses} ${colorClasses}`}
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 24 24"
      >
        <circle
          className="opacity-25"
          cx="12"
          cy="12"
          r="10"
          stroke="currentColor"
          strokeWidth="4"
        />
        <path
          className="opacity-75"
          fill="currentColor"
          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
        />
      </svg>
      {label && (
        <span className={`mt-2 text-sm ${colorClasses}`}>{label}</span>
      )}
    </div>
  );
};

// Dots Spinner Variant
const DotsSpinner = ({ size = 'md', color = 'primary', className = '' }) => {
  const dotSizes = {
    sm: 'h-1.5 w-1.5',
    md: 'h-2.5 w-2.5',
    lg: 'h-3.5 w-3.5',
    xl: 'h-4 w-4',
  };

  const colorClasses = colors[color] || colors.primary;
  const dotSize = dotSizes[size] || dotSizes.md;

  return (
    <div className={`inline-flex items-center gap-1 ${className}`}>
      {[0, 1, 2].map((i) => (
        <span
          key={i}
          className={`${dotSize} ${colorClasses} rounded-full animate-pulse`}
          style={{
            backgroundColor: 'currentColor',
            animationDelay: `${i * 0.15}s`,
          }}
        />
      ))}
    </div>
  );
};

// Full Page Loader
const PageLoader = ({ message = 'Loading...' }) => (
  <div className="fixed inset-0 bg-white/80 backdrop-blur-sm flex items-center justify-center z-50">
    <div className="text-center">
      <Spinner size="xl" color="primary" />
      <p className="mt-4 text-gray-600 font-medium">{message}</p>
    </div>
  </div>
);

// Skeleton Loader
const Skeleton = ({ className = '', variant = 'text', width, height }) => {
  const baseClasses = 'animate-pulse bg-gray-200 rounded';

  const variants = {
    text: 'h-4 w-full',
    title: 'h-6 w-3/4',
    avatar: 'h-10 w-10 rounded-full',
    thumbnail: 'h-24 w-24 rounded-lg',
    card: 'h-48 w-full rounded-xl',
    button: 'h-10 w-24 rounded-lg',
  };

  const style = {};
  if (width) style.width = width;
  if (height) style.height = height;

  return (
    <div
      className={`${baseClasses} ${variants[variant] || ''} ${className}`}
      style={style}
    />
  );
};

Spinner.Dots = DotsSpinner;
Spinner.Page = PageLoader;
Spinner.Skeleton = Skeleton;

export default Spinner;
